import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { authAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

const PURPLE = '#200854';
const ORANGE = '#FF6600';

const NajSportLogin: React.FC = () => {
  const { t } = useTranslation();
  const { emailLogin } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [step, setStep] = useState<'email' | 'password'>('email');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleEmailStep = async () => {
    if (!email.trim()) {
      setError(t('najsport.enterEmail'));
      return;
    }

    setIsLoading(true);
    setError('');
    try {
      const { requiresPassword } = await authAPI.checkEmail(email.trim());
      if (requiresPassword) {
        setStep('password');
      } else {
        const { onboardingCompleted } = await emailLogin(email.trim());
        (document.activeElement as HTMLElement)?.blur();
        navigate(onboardingCompleted ? '/events' : '/onboarding');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || t('najsport.loginError'));
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordStep = async () => {
    if (!password) {
      setError(t('najsport.enterPassword'));
      return;
    }

    setIsLoading(true);
    setError('');
    try {
      const { onboardingCompleted } = await emailLogin(email.trim(), password);
      (document.activeElement as HTMLElement)?.blur();
      navigate(onboardingCompleted ? '/events' : '/onboarding');
    } catch (err: any) {
      setError(err.response?.data?.error || t('najsport.loginError'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (step === 'email') {
      await handleEmailStep();
    } else {
      await handlePasswordStep();
    }
  };

  const handleBack = () => {
    setStep('email');
    setPassword('');
    setError('');
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8"
      style={{ background: `linear-gradient(160deg, ${PURPLE} 0%, #15043C 100%)` }}
    >
      <div className="max-w-md w-full space-y-8 bg-white rounded-2xl shadow-2xl p-8">
        <div className="flex flex-col items-center">
          <img src="/najsport-logo.png" alt="NajSport" className="h-24 w-auto mb-4" />
          <h2 className="mt-2 text-center text-3xl font-extrabold" style={{ color: PURPLE }}>
            {t('najsport.loginTitle')}
          </h2>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          {step === 'email' ? (
            <div>
              <label htmlFor="email" className="block text-sm font-medium mb-1" style={{ color: PURPLE }}>
                {t('najsport.loginEmail')}
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="appearance-none relative block w-full px-4 py-3 border-2 border-gray-200 placeholder-gray-400 text-gray-900 rounded-lg focus:outline-none focus:ring-2 focus:z-10 sm:text-sm transition-colors"
                style={{ '--tw-ring-color': PURPLE } as any}
                placeholder="you@example.com"
              />
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleBack}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                  aria-label="Back"
                >
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <span className="text-sm text-gray-500">{email}</span>
              </div>
              <div>
                <label htmlFor="password" className="block text-sm font-medium mb-1" style={{ color: PURPLE }}>
                  {t('najsport.loginPassword')}
                </label>
                <input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  autoFocus
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="appearance-none relative block w-full px-4 py-3 border-2 border-gray-200 placeholder-gray-400 text-gray-900 rounded-lg focus:outline-none focus:ring-2 focus:z-10 sm:text-sm transition-colors"
                  style={{ '--tw-ring-color': PURPLE } as any}
                  placeholder={t('najsport.loginPassword')}
                />
              </div>
            </div>
          )}

          <button
            type="submit"
            disabled={isLoading}
            className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-bold rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all hover:shadow-lg"
            style={{ backgroundColor: ORANGE }}
          >
            {isLoading ? (
              <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
            ) : step === 'email' ? (
              t('najsport.loginContinue')
            ) : (
              t('najsport.loginSubmit')
            )}
          </button>
        </form>

        <div className="text-center text-xs text-gray-400 mt-4 space-y-1">
          <p>Powered by NajSport</p>
          <button
            type="button"
            onClick={() => navigate('/privacy-policy')}
            className="text-gray-400 hover:text-gray-600 underline transition-colors"
          >
            Ochrana osobných údajov
          </button>
        </div>
      </div>
    </div>
  );
};

export default NajSportLogin;
